import { useMemo, useState } from "react";
import {
  getListInventoryItemsQueryKey,
  getListPlanEntitlementsQueryKey,
  useCreatePlanEntitlement,
  useDeletePlanEntitlement,
  useListInventoryCategories,
  useListInventoryItems,
  useListPlanEntitlements,
  useUpdatePlanEntitlement,
  type PlanEntitlement,
  QuotaPeriod,
} from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { Plus, Trash2 } from "lucide-react";

/**
 * StationaryItemsTable — per-item quota table for inventory_items in the
 * "Stationery" category. Backed by plan_entitlements. Operator picks an
 * item from a Combobox (already-picked items filtered out — no repeats),
 * sets Quota Quantity, Quota Period, and Overage Rate. Rows auto-save
 * on blur. Delete trash icon removes the entitlement.
 *
 * Requires planId — in create-mode (no planId yet) the parent renders
 * a "Save plan first" hint instead.
 */

const QUOTA_PERIOD_LABELS: Record<QuotaPeriod, string> = {
  per_day: "per day",
  per_month: "per month",
  per_lifetime: "per lifetime",
  per_membership: "per membership",
  per_visit: "per visit",
  per_booking: "per booking",
  per_hour: "per hour",
};

export interface StationaryItemsTableProps {
  planId: number;
  spaceId: number;
}

export function StationaryItemsTable({ planId, spaceId }: StationaryItemsTableProps) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [addPickerOpen, setAddPickerOpen] = useState(false);

  // 1. Find Stationery categories (multiple rows exist in the DB — match by name)
  const categoriesQuery = useListInventoryCategories();
  const stationeryCategoryIds = useMemo(
    () =>
      (categoriesQuery.data ?? [])
        .filter((c) => /station(e|a)ry/i.test(c.name))
        .map((c) => c.id),
    [categoriesQuery.data],
  );

  // 2. All inventory items — filter client-side to Stationery
  const itemsQuery = useListInventoryItems({});
  const stationeryItems = useMemo(
    () =>
      (itemsQuery.data ?? [])
        .filter((i) => stationeryCategoryIds.includes(i.categoryId))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [itemsQuery.data, stationeryCategoryIds],
  );
  const itemById = useMemo(
    () => Object.fromEntries(stationeryItems.map((i) => [i.id, i])),
    [stationeryItems],
  );

  // 3. Existing plan entitlements for this plan, filtered to Stationery items
  const entitlementsQuery = useListPlanEntitlements({ planId });
  const stationeryEntitlements = useMemo(
    () =>
      (entitlementsQuery.data ?? []).filter((e) =>
        stationeryCategoryIds.includes(itemById[e.itemId]?.categoryId ?? -1),
      ),
    [entitlementsQuery.data, itemById, stationeryCategoryIds],
  );

  // Dedupe by item NAME — multiple inventory items can share the same display
  // name (Doms vs Eduway "Duster & Marker"); operator wants one row per
  // unique name. Keep the lowest-id entitlement as the visible representative.
  const visibleEntitlements = useMemo(() => {
    const seenNames = new Set<string>();
    const out: typeof stationeryEntitlements = [];
    for (const e of stationeryEntitlements) {
      const name = itemById[e.itemId]?.name?.trim() ?? "";
      if (!name) {
        out.push(e); // unknown item → keep visible so operator can clean it up
        continue;
      }
      if (seenNames.has(name)) continue;
      seenNames.add(name);
      out.push(e);
    }
    return out;
  }, [stationeryEntitlements, itemById]);

  // Picker also dedupes by name: once any "Marker" is in the table, no other
  // Marker variant is offered. Already-picked names are blocked.
  const pickedNames = new Set(
    visibleEntitlements
      .map((e) => itemById[e.itemId]?.name?.trim() ?? "")
      .filter(Boolean),
  );
  const pickedItemIds = new Set(stationeryEntitlements.map((e) => e.itemId));
  const seenAvailableNames = new Set<string>();
  const availableItems = stationeryItems.filter((i) => {
    if (pickedItemIds.has(i.id)) return false;
    const name = i.name.trim();
    if (pickedNames.has(name)) return false;
    if (seenAvailableNames.has(name)) return false;
    seenAvailableNames.add(name);
    return true;
  });

  const createEntitlement = useCreatePlanEntitlement();
  const updateEntitlement = useUpdatePlanEntitlement();
  const deleteEntitlement = useDeletePlanEntitlement();

  function invalidate() {
    qc.invalidateQueries({ queryKey: getListPlanEntitlementsQueryKey({ planId }) });
    qc.invalidateQueries({ queryKey: getListInventoryItemsQueryKey({}) });
  }

  async function addLineItem(itemId: number) {
    setAddPickerOpen(false);
    try {
      await createEntitlement.mutateAsync({
        data: {
          spaceId,
          planId,
          itemId,
          quotaQuantity: 0,
          quotaPeriod: "per_month",
          overageRate: null,
          carryForward: false,
        },
      });
      invalidate();
    } catch (err: unknown) {
      toast({
        title: "Couldn't add item",
        description: (err as Error).message ?? "Try again.",
        variant: "destructive",
      });
    }
  }

  async function patchEntitlement(
    e: PlanEntitlement,
    patch: { quotaQuantity?: number; quotaPeriod?: QuotaPeriod; overageRate?: number | null },
  ) {
    try {
      await updateEntitlement.mutateAsync({ id: e.id, data: patch });
      invalidate();
    } catch (err: unknown) {
      toast({
        title: "Couldn't update",
        description: (err as Error).message ?? "Try again.",
        variant: "destructive",
      });
    }
  }

  async function removeEntitlement(e: PlanEntitlement) {
    try {
      await deleteEntitlement.mutateAsync({ id: e.id });
      invalidate();
    } catch (err: unknown) {
      toast({
        title: "Couldn't delete",
        description: (err as Error).message ?? "Try again.",
        variant: "destructive",
      });
    }
  }

  return (
    <div className="space-y-3 max-w-3xl">
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Item</TableHead>
              <TableHead className="w-32">Quota Amount</TableHead>
              <TableHead className="w-44">Quota Period</TableHead>
              <TableHead className="w-32">Overage Rate (₹)</TableHead>
              <TableHead className="w-12" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {visibleEntitlements.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="h-16 text-center text-xs text-muted-foreground">
                  No stationery items yet. Click <em>Add Line Item</em> below.
                </TableCell>
              </TableRow>
            ) : (
              visibleEntitlements.map((e) => {
                const item = itemById[e.itemId];
                return (
                  <TableRow key={e.id}>
                    <TableCell>
                      {item ? (
                        <span className="font-medium text-sm">{item.name}</span>
                      ) : (
                        <span className="text-sm text-muted-foreground">Item #{e.itemId}</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Input
                        type="number"
                        min={0}
                        step="0.01"
                        defaultValue={e.quotaQuantity}
                        onBlur={(ev) => {
                          const next = Number(ev.target.value);
                          if (Number.isFinite(next) && next !== e.quotaQuantity) {
                            patchEntitlement(e, { quotaQuantity: next });
                          }
                        }}
                        className="h-8 text-sm"
                      />
                    </TableCell>
                    <TableCell>
                      <Select
                        value={e.quotaPeriod}
                        onValueChange={(v) =>
                          patchEntitlement(e, { quotaPeriod: v as QuotaPeriod })
                        }
                      >
                        <SelectTrigger className="h-8 text-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(QUOTA_PERIOD_LABELS).map(([k, label]) => (
                            <SelectItem key={k} value={k}>{label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Input
                        type="number"
                        min={0}
                        step="0.01"
                        defaultValue={e.overageRate ?? ""}
                        placeholder="—"
                        onBlur={(ev) => {
                          const v = ev.target.value.trim();
                          const next = v === "" ? null : Number(v);
                          if (next !== e.overageRate) {
                            patchEntitlement(e, { overageRate: next });
                          }
                        }}
                        className="h-8 text-sm"
                      />
                    </TableCell>
                    <TableCell>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => removeEntitlement(e)}
                        title="Remove item"
                        className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      <Popover open={addPickerOpen} onOpenChange={setAddPickerOpen}>
        <PopoverTrigger asChild>
          <Button
            size="sm"
            variant="outline"
            disabled={availableItems.length === 0 || createEntitlement.isPending}
          >
            <Plus className="mr-1 h-3.5 w-3.5" />
            Add Line Item
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[320px] p-0" align="start">
          <Command>
            <CommandInput placeholder="Search stationery items…" />
            <CommandList>
              <CommandEmpty>No matches.</CommandEmpty>
              <CommandGroup>
                {availableItems.map((item) => {
                  // Search still matches on brand under the hood so duplicates
                  // like "Marker" with different brands stay distinguishable
                  // via typed query, but only the name renders.
                  const searchValue = [item.brand, item.name].filter(Boolean).join(" ");
                  return (
                    <CommandItem
                      key={item.id}
                      value={searchValue}
                      onSelect={() => addLineItem(item.id)}
                    >
                      <span className="font-medium text-sm">{item.name}</span>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {availableItems.length === 0 && stationeryItems.length > 0 && (
        <p className="text-[10px] text-muted-foreground">
          All stationery items already added. Remove a row to free one up.
        </p>
      )}
    </div>
  );
}
